import type { OpenGenericDialogFunc } from "src/components/common/dialogs/GenericDialogContext.ts";
import type { OpenInputDialogFunc } from "src/components/common/dialogs/input/useInputDialog.tsx";
import { openFormOnDialog } from "src/components/common/dialogs/formDialog/openFormOnDialog.ts";
import { ControlChartLimitCalculationForm } from "./ControlChartLimitCalculationForm.tsx";
import { ControlChartLimitsDataGrid } from "./ControlChartLimitsDataGrid.tsx";
import { ControlChartGroupBoxPlotView } from "./ControlChartGroupBoxPlotView.tsx";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";
import i18n from "i18next";
import { controlChartLimitCalculationFormDialogContextKey } from "./controlChartAnalysisAgentContextKeys.ts";

export function openControlChartLimitsDialog({
	openDialog,
	controlChartId,
	title = i18n.t("control_limits"),
}: {
	openDialog: OpenGenericDialogFunc;
	controlChartId: number;
	title?: string;
}) {
	openDialog({
		title,
		size: "xl",
		content: <ControlChartLimitsDataGrid controlChartId={controlChartId} />,
		agentContext: {
			key: defineAgentUiContextKey("spc.controlChart.limits.dialog"),
			data: { controlChartId },
			pathSegments: [i18n.t("control_limits")],
		},
	});
}

export function openControlChartLimitCalculationDialog({
	openDialog,
	controlChartId,
	recordId,
	onSubmit,
}: {
	openDialog: OpenGenericDialogFunc;
	controlChartId: number;
	recordId?: number;
	onSubmit?: () => void | Promise<unknown>;
}) {
	openFormOnDialog({
		openDialog,
		title: i18n.t("calculate_control_limits"),
		size: "lg",
		component: ControlChartLimitCalculationForm,
		props: { controlChartId, recordId },
		onSubmit,
		agentContext: {
			key: controlChartLimitCalculationFormDialogContextKey,
			data: { controlChartId, recordId: recordId ?? null },
		},
	});
}

export async function askInputAndOpenControlChartGroupBoxPlot({
	showInputDialog,
	openDialog,
	chartGroupId,
	chartGroupName,
}: {
	showInputDialog: OpenInputDialogFunc;
	openDialog: OpenGenericDialogFunc;
	chartGroupId: number;
	chartGroupName: string;
}) {
	const recordCount = await showInputDialog({
		type: "integer",
		title: i18n.t("sample_size"),
		required: true,
		defaultValue: 50,
	});
	if (recordCount == null) return;
	openDialog({
		title: chartGroupName,
		size: "xl",
		content: <ControlChartGroupBoxPlotView chartGroupId={chartGroupId} recordCount={recordCount} />,
		agentContext: {
			key: defineAgentUiContextKey("spc.controlChart.groupBoxPlot.dialog"),
			data: { chartGroupId, recordCount },
			pathSegments: [i18n.t("box_plot")],
		},
	});
}
